import axios from "axios";
import {applyTunings} from "@knockout-game/engine";
import {useMainStore} from "@/store";
import StorageManager from "./storageManager";
import ImageCacheManager from "./imageCacheManager";
import request from "@/services/request";

export default class ReferenceManager
{
    static async check()
    {
        return new Promise((resolve, reject) => {
            request('references')
                .then(({data}) => {
                    resolve(data);
                })
                .catch(e => {
                    reject(e);
                });
        });
    }

    static async load()
    {
        return new Promise((resolve, reject) => {
            ReferenceManager.check()
                .then(reference_id => {
                    ReferenceManager.get(reference_id)
                        .then(() => {
                            resolve();
                        }).catch(e => {
                            reject(e);
                        });
                }).catch(e => {
                    // TODO: EventBus.emit() TO App FOR DISPLAY AS toastError
                    reject(e);
                });
        });
    }

    // TODO: THIS ALWAYS OVERRIDES STORE, EVEN IF ALREADY SET
    static async get(reference_id)
    {
        useMainStore().SET_REFERENCES_LOADING(true);
        return new Promise((resolve, reject) => {
            ReferenceManager.getFromSources(reference_id)
                .then(data => {
                    ReferenceManager.addToStore(reference_id, data);
                    resolve();
                }).catch(e => {
                    reject(e);
                })
                .finally(() => {
                    useMainStore().SET_REFERENCES_LOADING(false);
                });
        });
    }

    static async getFromSources(reference_id)
    {
        return new Promise((resolve, reject) => {
            ReferenceManager.getFromLocalStorage(reference_id)
                .then(data => {
                    resolve(data);
                }).catch(() => {
                    ReferenceManager.getFromCDN(reference_id)
                        .then(data => {
                            resolve(data);
                        }).catch(e => {
                            reject(e);
                        });
                });
        });
    }

    static async getFromLocalStorage(reference_id)
    {
        return new Promise((resolve, reject) => {
            if (! StorageManager.has('references')) {
                return reject();
            }
            const references = JSON.parse(StorageManager.get('references'));
            if (! Object.prototype.hasOwnProperty.call(references, 'reference_id')
                || ! Object.prototype.hasOwnProperty.call(references, 'data')
                || references.reference_id !== reference_id) {
                return reject();
            }
            return resolve(references.data);
        });
    }

    static async getFromCDN(reference_id)
    {
        return new Promise((resolve, reject) => {
            axios.get(import.meta.env.VITE_REFERENCES_PATH + reference_id + '.json')
                .then(({data}) => {
                    StorageManager.set('references', JSON.stringify({reference_id, data}));
                    resolve(data);
                }).catch(e => {
                    reject(e);
            })
        });
    }

    static addToStore(reference_id, data)
    {
        useMainStore().SET_REFERENCES_ID(reference_id);
        useMainStore().SET_REFERENCES_DATA(data);
        useMainStore().SET_REFERENCES_LOADED(true);

        // Push database-driven engine tunings into @knockout-game/engine so the
        // client's prediction/animation uses the same balance values as the
        // authoritative gameserver. This is the single point every reference
        // load funnels through (boot, header refresh, ReferenceFile broadcast),
        // and it runs before any per-bout Player/Bot is constructed in Bout.vue.
        ReferenceManager.applyTunings(data);

        ImageCacheManager.cacheDesignImages(reference_id, data);
    }

    /**
     * Applies the design.tuning reference collection to the engine. The
     * collection is an array of { key, value } rows; reduce it to a
     * { key: value } map and hand it to the engine. Missing/empty leaves the
     * engine's shipped defaults in place.
     */
    static applyTunings(data)
    {
        const tunings = data?.design?.tuning;
        if (! Array.isArray(tunings) || tunings.length === 0) {
            return;
        }
        const map = tunings.reduce((acc, row) => {
            if (row && row.key !== undefined && row.key !== null) {
                acc[row.key] = row.value;
            }
            return acc;
        }, {});
        applyTunings(map);
    }
}
